import { hora } from '../lib/formato';
import { tx } from '../i18n';
import type { PuntoSalud } from '../tipos';

/**
 * Las últimas 24 h de CPU, memoria y disco, dibujadas a mano en SVG: son tres series de
 * porcentajes sobre el mismo eje: no hace falta una librería de gráficos para eso.
 *
 * Lo que el appliance no mandó no se inventa: si entre dos medidas pasaron más de 15 min
 * (estuvo sin contacto), la línea se corta en vez de cruzar el hueco con una recta que
 * nunca existió.
 *
 * `hasta` es el borde derecho: la hora en que llegaron estos datos. Va por fuera a
 * propósito, para que el dibujo no dependa del reloj en el momento de pintar.
 */

const AN = 720;
const AL = 220;
const IZQ = 38;
const DER = 10;
const ARR = 12;
const ABA = 24;
const HUECO_MS = 15 * 60_000;

const SERIES = [
  { clave: 'cpu_pc', nombre: 'CPU', color: 'var(--cpu)' },
  { clave: 'memoria_pc', nombre: 'Memory', color: 'var(--mem)' },
  { clave: 'disco_pc', nombre: 'Disk', color: 'var(--disco)' },
] as const;

export function Salud24h({ puntos, hasta, horas = 24 }: { puntos: PuntoSalud[]; hasta: number; horas?: number }) {
  const ahora = hasta;
  const desde = ahora - horas * 3600_000;
  // Solo lo que cae dentro de la ventana y tiene hora válida, en orden.
  const datos = puntos
    .map((p) => ({ t: Date.parse(p.tiempo), d: p.datos }))
    .filter((p) => Number.isFinite(p.t) && p.t >= desde)
    .sort((a, b) => a.t - b.t);

  const x = (t: number) => IZQ + ((t - desde) / (ahora - desde)) * (AN - IZQ - DER);
  const y = (v: number) => ARR + (1 - Math.min(100, Math.max(0, v)) / 100) * (AL - ARR - ABA);

  /** Los tramos seguidos de una serie: cada uno es una línea aparte. */
  const tramos = (clave: (typeof SERIES)[number]['clave']) => {
    const salida: string[] = [];
    let actual: string[] = [];
    let anterior = 0;
    for (const p of datos) {
      const v = p.d[clave];
      if (typeof v !== 'number') continue;
      if (actual.length && p.t - anterior > HUECO_MS) {
        salida.push(actual.join(' '));
        actual = [];
      }
      actual.push(`${actual.length ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(v).toFixed(1)}`);
      anterior = p.t;
    }
    if (actual.length) salida.push(actual.join(' '));
    return salida;
  };

  // Una marca de hora cada 6 h, empezando por la más vieja.
  const marcas = [0, 0.25, 0.5, 0.75, 1].map((f) => desde + f * (ahora - desde));

  return (
    <figure className="grafico">
      <svg viewBox={`0 0 ${AN} ${AL}`} role="img" aria-label={tx.salud.titulo(horas)}>
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line className="g-rejilla" x1={IZQ} x2={AN - DER} y1={y(v)} y2={y(v)} />
            <text className="g-eje" x={IZQ - 8} y={y(v) + 4} textAnchor="end">
              {v}
            </text>
          </g>
        ))}
        {marcas.map((t) => (
          <text key={t} className="g-eje" x={x(t)} y={AL - 6} textAnchor="middle">
            {hora(t)}
          </text>
        ))}
        {SERIES.map((s) => tramos(s.clave).map((d, i) => <path key={`${s.clave}-${i}`} d={d} fill="none" stroke={s.color} strokeWidth="2" />))}
      </svg>
      {datos.length === 0 && <p className="grafico-vacio">{tx.salud.sinDatos(horas)}</p>}
      <figcaption className="leyenda">
        {SERIES.map((s) => (
          <span key={s.clave}>
            <i style={{ background: s.color }} aria-hidden="true" />
            {s.nombre}
          </span>
        ))}
        <span className="d">{tx.salud.pie}</span>
      </figcaption>
    </figure>
  );
}
